import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Sparkles, Loader2 } from "lucide-react";

export default function NoteQualitySettings() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const [test, setTest] = useState("");
  const [testRes, setTestRes] = useState(null);
  const [testing, setTesting] = useState(false);

  useEffect(() => { api.get("/settings/note-quality").then((r) => setS(r.data)).catch(() => {}); }, []);

  const set = (k) => (v) => setS((p) => ({ ...p, [k]: v }));

  const save = async () => {
    setBusy(true);
    try { await api.put("/settings/note-quality", s); toast.success("Impostazioni salvate"); }
    catch { toast.error("Errore nel salvataggio"); }
    finally { setBusy(false); }
  };

  const runTest = async () => {
    setTesting(true); setTestRes(null);
    try { const r = await api.post("/activities/check-note", { text: test }); setTestRes(r.data); }
    catch { toast.error("Errore nel test"); }
    finally { setTesting(false); }
  };

  if (!s) return null;

  return (
    <div className="mx-auto max-w-3xl space-y-6" data-testid="note-quality-page">
      <div className="flex items-center gap-2">
        <Sparkles className="h-5 w-5 text-primary" />
        <h1 className="font-heading text-3xl font-black tracking-tight">Controllo Qualità Note (AI)</h1>
      </div>
      <p className="text-sm text-muted-foreground">
        Verifica automatica delle note inserite nelle attività: l'AI controlla che siano <b>complete e costruttive</b>
        (cosa è stato fatto/emerso, esito e prossimi passi). Le note vaghe come "ok", "fatto", "tutto bene" vengono segnalate.
      </p>

      <Card className="space-y-5 p-6">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="space-y-1.5">
            <Label className="text-xs">Controllo attivo</Label>
            <Select value={s.enabled ? "on" : "off"} onValueChange={(v) => set("enabled")(v === "on")}>
              <SelectTrigger data-testid="nq-enabled"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="on">Attivo</SelectItem><SelectItem value="off">Disattivato</SelectItem></SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Comportamento</Label>
            <Select value={s.mode} onValueChange={set("mode")}>
              <SelectTrigger data-testid="nq-mode"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="blocca">Blocca il salvataggio</SelectItem>
                <SelectItem value="avvisa">Solo avviso (non blocca)</SelectItem>
              </SelectContent>
            </Select>
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Lunghezza minima nota (caratteri)</Label>
            <Input type="number" min={0} value={s.min_length} onChange={(e) => set("min_length")(parseInt(e.target.value || "0", 10))} data-testid="nq-minlength" />
          </div>
          <div className="space-y-1.5">
            <Label className="text-xs">Applica solo alle attività "svolte"</Label>
            <Select value={s.only_svolto ? "on" : "off"} onValueChange={(v) => set("only_svolto")(v === "on")}>
              <SelectTrigger data-testid="nq-onlysvolto"><SelectValue /></SelectTrigger>
              <SelectContent><SelectItem value="on">Sì</SelectItem><SelectItem value="off">No (anche pianificate)</SelectItem></SelectContent>
            </Select>
          </div>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs">Criteri aggiuntivi (facoltativo)</Label>
          <Textarea value={s.istruzioni || ""} onChange={(e) => set("istruzioni")(e.target.value)} placeholder="Es. richiedi sempre il nome dell'interlocutore e la data del prossimo contatto." data-testid="nq-istruzioni" />
        </div>
        <div className="flex justify-end">
          <Button onClick={save} disabled={busy} data-testid="nq-save">{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Salva impostazioni"}</Button>
        </div>
      </Card>

      <Card className="space-y-3 p-6">
        <h2 className="font-heading text-lg font-bold tracking-tight">Prova il controllo</h2>
        <Textarea value={test} onChange={(e) => setTest(e.target.value)} placeholder="Scrivi una nota di prova…" data-testid="nq-test-input" />
        <div className="flex items-center gap-3">
          <Button variant="outline" onClick={runTest} disabled={testing} data-testid="nq-test-btn">
            {testing ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Sparkles className="mr-2 h-4 w-4" />} Verifica con AI
          </Button>
          {testRes && (
            <span className={`text-sm font-semibold ${testRes.sufficiente ? "text-green-400" : "text-amber-400"}`} data-testid="nq-test-result">
              {testRes.sufficiente ? "✓ Nota adeguata" : "✗ Nota insufficiente"}
            </span>
          )}
        </div>
        {testRes && <p className="text-xs text-muted-foreground" data-testid="nq-test-motivo">{testRes.motivo}</p>}
      </Card>
    </div>
  );
}
